import { dateUSToDateISO } from './date'
import {
    t_date_time_not_set,
    t_element,
    t_element_empty_info,
    t_element_error_info,
    t_element_partsynced_info,
    t_element_synced_info,
    t_element_tobedeleted_info,
    t_element_tobesynced_info,
    t_element_updating_info,
    t_esign, t_layer,
    t_layer_not_set,
    t_url_not_set
} from '../constants/publish'
import { secondsToTimeString } from './time'
import { titles } from '../vue/components/content/elements/types';

/**
 * Calculate Publish Element duration
 * @param data {PublishElementData}
 * @returns {number} Calculated duration in seconds
 */
export function calcDuration(data) {

    let elementDuration = 0;

    if (data.duration_type == 2) {
        const pagedDuration = calcPagedDuration(data)
        elementDuration += (pagedDuration == '?' ? 0 : pagedDuration) * (+data.display_runs || 0);
    } else if (data.duration_type == 1 && data.duration_original != null) {
        elementDuration += +data.duration_original || 0;
    } else {
        elementDuration += +data.duration || 0;
    }

    return elementDuration;
}

/**
 * Calculate Publish Element duration when duration type is 2
 * @param data {PublishElementData}
 * @returns {number | '?'} Calculated duration in seconds
 */
export function calcPagedDuration(data) {

    if (data.type == 'ppt' || data.type == 'pdf') {
        const page_to = parseInt((!isNaN(data.page_to) && data.page_to) ? data.page_to : data.page_count);
        const page_from = parseInt((!isNaN(data.page_from) && data.page_from) ? data.page_from : 1);
        const interval = parseInt((!isNaN(data.interval) && data.interval) ? data.interval : 11);

        if (interval && page_from && page_to) {
            return Math.max(interval * (page_to - page_from + 1), 0) ?? '?'
        } else {
            return '?';
        }
    } else {
        if (data.duration_original == 'unknown' || !data.duration_original || data.type == 'rssfeed' ) {
            return '?';
        } else {
            return data.duration_original;
        }
    }
}

/**
 * Check whether Publish Element's Data satisfies Filters
 * @param elementData {PublishElementData}
 * @param filters {AdaptedPublishFilters}
 * @param screengroupsToScreens {Record.<number, number[]>}
 * @param editPrivilege {boolean}
 * @returns {boolean} True if Element's Data satisfies Filters (is shown), false otherwise
 */
export function checkElementSatisfiesFilters(elementData, filters, screengroupsToScreens, editPrivilege = true) {

    // Check date limits

    // Check element valid_from
    if (filters.validTo) {
        const validFromISO = dateUSToDateISO(elementData.valid_from || elementData.valid_from_placeholder)
        if (validFromISO && ((new Date(validFromISO)) > filters.validTo)) // If element starts being valid after the max valid filtered date, it does not pass the check
            return false
    }

    // Check element valid_to
    if (filters.validFrom) {
        const validToISO = dateUSToDateISO(elementData.valid_to || elementData.valid_to_placeholder)
        if (validToISO && ((new Date(validToISO)) < filters.validFrom)) // If element stops being valid before the min valid filtered date, it does not pass the check
            return false
    }

    // Check screen and screengroup matches

    // If element has no screens and no screengroups assigned, it passes screen and screengroup filtering
    if (!elementData.screens?.length && !elementData.screengroups?.length && editPrivilege)
        return true

    if (filters.screen) {
        // Check if element is assigned to the filtered screen
        if (elementData.screens?.includes(filters.screen))
            return true

        // Check if element is assigned to any of the screengroups that contain the filtered screen
        if (elementData.screengroups?.some(screengroup => screengroupsToScreens[screengroup]?.includes(+filters.screen)))
            return true

    } else if (filters.screengroup) {
        // Check if element is assigned to the filtered screengroup
        if (elementData.screengroups?.includes(filters.screengroup))
            return true

        // Check if element is assigned to any of the screens in the filtered screengroup
        if (elementData.screens?.some(screen => screengroupsToScreens[filters.screengroup]?.includes(+screen)))
            return true

        // Check if element is assigned to any of the screens in the filtered screengroup
        // (left here as placeholder, original code has this, but the check is equal to the one above)
        //if (screengroupsToScreens[filters.screengroup]?.some(screen => elementData.screens?.includes(screen)))
        //    return true

        // Check if element is assigned to any of the screengroups that contain any screen in the filtered screengroup
        if (screengroupsToScreens[filters.screengroup]?.some(screen => elementData.screengroups?.some(screengroup => screengroupsToScreens[screengroup]?.includes(screen))))
            return true

    } else // if both the screen and screengroup filters are empty, the element passes the check
        return true

    // If execution reaches this point, then element does not match required conditions
    return false
}

/**
 * Get color assigned to PublishState
 * @param state {PublishState}
 * @returns {string} Css Color (e.g. '#ff0000')
 */
export function getStateColor(state) {
    switch (state) {
        case 'synced':
            return getComputedStyle(document.body).getPropertyValue('--color-state-synced');
        case 'partsynced':
            return getComputedStyle(document.body).getPropertyValue('--color-state-partsynced');
        case 'tobesynced':
            return getComputedStyle(document.body).getPropertyValue('--color-state-tobesynced');
        case 'updating':
            return getComputedStyle(document.body).getPropertyValue('--color-state-updating');
        case 'error':
            return getComputedStyle(document.body).getPropertyValue('--color-state-error');
        case 'deleted':
        case 'fulldelete':
        case 'partdeleted':
        case 'tobedeleted':
            return getComputedStyle(document.body).getPropertyValue('--color-state-deleted');
        case 'empty':
        default:
            return getComputedStyle(document.body).getPropertyValue('--color-state-empty');
    }
}

/**
 * Get icon assigned to PublishState
 * @param state {PublishState}
 * @returns {string} Icon (e.g. 'fa-check')
 */
export function getStateIcon(state) {
    switch (state) {
        case 'synced':
            return 'fa-paper-plane'
        case 'partsynced':
            return 'fa-octagon-check'
        case 'tobesynced':
            return 'fa-paper-plane'
        case 'updating':
            return 'fa-arrows-rotate fa-spin'
        case 'error':
            return 'fa-circle-exclamation'
        case 'deleted':
        case 'fulldelete':
        case 'partdeleted':
        case 'tobedeleted':
            return 'fa-trash'
        case 'empty':
        default:
            return 'fa-circle'
    }
}

/**
 * Get color of text assigned to PublishState
 * @param state {PublishState}
 * @returns {string} Css Color (e.g. '#ff0000')
 */
export function getStateTextColor(state) {
    switch (state) {
        case 'partsynced':
        case 'tobesynced':
        case 'updating':
        case 'empty':
            return 'black'
        default:
            return 'white'
    }
}

/**
 * Get message to show in descriptions of PublishState
 * @param state {PublishState}
 * @returns {string} Translated string ready to insert into html
 */
export function getStateMessage(state) {
    switch (state) {
        case 'synced':
            return t_element_synced_info
        case 'partsynced':
            return t_element_partsynced_info
        case 'tobesynced':
            return t_element_tobesynced_info
        case 'updating':
            return t_element_updating_info
        case 'error':
            return t_element_error_info
        case 'deleted':
        case 'fulldelete':
        case 'tobedeleted':
            return t_element_tobedeleted_info
        case 'partdeleted':
            return `${t_element_tobedeleted_info} ${t_element_partsynced_info}`
        case 'empty':
            return t_element_empty_info
        default:
            return ''
    }
}

/**
 * Get class to apply to tooltip borders of PublishState
 * @param state {PublishState}
 * @returns {string} Class name
 */
export function getStateBorderClass(state) {
    switch (state) {
        case 'synced':
            return 'tooltip-border-synced'
        case 'partsynced':
            return 'tooltip-border-partsynced'
        case 'tobesynced':
            return 'tooltip-border-tobesynced'
        case 'updating':
            return 'tooltip-border-updating'
        case 'error':
            return 'tooltip-border-error'
        case 'deleted':
        case 'fulldelete':
        case 'partdeleted':
        case 'tobedeleted':
            return 'tooltip-border-deleted'
        case 'empty':
        default:
            return 'tooltip-border-empty'
    }
}

/**
 * Calculate whether element size differs from layer size
 * @param elementWidth {number}
 * @param elementHeight {number}
 * @param layerWidth {number}
 * @param layerHeight {number}
 * @returns {boolean} True is sizes are different, false if sizes are the same or element has no size
 */
export function elementSizeDifferentFromLayerSize(elementWidth, elementHeight, layerWidth, layerHeight) {
    return elementWidth && elementHeight &&
        !(elementWidth == layerWidth && elementHeight == layerHeight)
}


/**
 * Adapt {@link PublishLayer} for use in frontend
 * @param layer {PublishLayer}
 * @param layerIndex {number}
 * @param filteredElementIndicesMap {Record<number, boolean>}
 * @param provideHrefCallback {(layerIndex: number, elementIndex: number) => string}
 * @returns {PublishTableLayer} Adapted layer
 */
export function adaptPublishLayer(layer, layerIndex, filteredElementIndicesMap, provideHrefCallback) {

    const filteredElements = layer.elements?.filter((element, elementIndex) => filteredElementIndicesMap[elementIndex])

    const duration = filteredElements?.reduce(
        (accumulator, publishElement) => accumulator + ((!publishElement.data.filter_hidden) ? calcDuration(publishElement.data) : 0)
        , 0) || 0

    const totalElements = filteredElements?.length || 0
    const approvedElements = filteredElements?.reduce((accumulator, publishElement) => accumulator + ((publishElement.data.approved && !publishElement.data.filter_hidden) ? 1 : 0), 0) || 0

    const anyElementsNotSynced = !!filteredElements?.some(element => element.state != 'synced')

    const state = !filteredElements?.length ? 'empty' : anyElementsNotSynced ? 'tobesynced' : 'synced'

    const layerAspectRatio = (layer.width != undefined && layer.height > 0) ? (layer.width / layer.height) : 1

    /** @type {PublishTableLayer} */
    const tableLayer = {
        id: layer.id,
        index: layerIndex,
        layer: layer.fullname,
        state,
        stateColor: getStateColor(state),
        stateTextColor: getStateTextColor(state),
        resolution: `${layer.width}x${layer.height}`,
        aspectRatio: layerAspectRatio,
        duration,
        durationString: secondsToTimeString(duration),
        approvedElements,
        unApprovedElements: totalElements - approvedElements,
        anyUnApproved: totalElements != approvedElements,
        totalElements,
        approvedOverTotal: totalElements ? (approvedElements / totalElements) : 0,
        href: provideHrefCallback(layer.id),
        /* Must be layer.elements to contain all elements, they are v-ifed in template. Only way to create enter/leave transitions */
        elements: layer.elements?.map((element, elementIndex) => {

            const elementAspectRatio = (element.data.width != undefined && element.data.height != undefined) ? (+element.data.width / +element.data.height) : layerAspectRatio
            /** @type {PublishTableElement} */
            const newElement = {
                id: element.id,
                data: element.data,
                state: element.state,
                aspectRatio: elementAspectRatio,
                href: provideHrefCallback(layer.id, elementIndex),
                alt: `${t_layer}: ${layer.fullname}, ${t_element}: ${element.data.name ?? elementIndex}`
            }
            return newElement
        }),
        width: layer.width,
        height: layer.height
    }

    return tableLayer
}

function titleBracketsOptionalDescription(title, description) {
    if (description)
        return `[${title}] ${description}`
    else
        return title
}

/**
 * Get description for {@link PublishElementData}
 * @param elementData {PublishElementData}
 * @returns {string | null} Description for PublishElementData
 */
export function getDescriptionForElement(elementData) {
    let elementDescriptionTemp = elementData.description;

    // The fallthrough to default is intended for emptyrectangle and text
    // noinspection FallThroughInSwitchStatementJS
    switch (elementData.type) {
        case 'campaign':
            return titleBracketsOptionalDescription(elementData.name, elementData.description)

        case 'countdown':
            return titleBracketsOptionalDescription(
                (!elementData.countdown_date || !elementData.countdown_time) ?
                    t_date_time_not_set :
                    (`${elementData.countdown_date?.trim()} ${elementData.countdown_time?.trim()}`),
                elementData.description
            );

        case 'esign':
            return titleBracketsOptionalDescription(`${elementData.esign_type.substring(2)}${elementData.esign_name ? ` - ${elementData.esign_name}` : ''}`, elementData.description);

        case 'iframe':
            return titleBracketsOptionalDescription(elementData.iframe_url?.trim() || t_url_not_set, elementData.description);

        case 'layer':
        {
            if (!elementData.layer) {
                return titleBracketsOptionalDescription(t_layer_not_set, elementData.description)
            } else if (elementData.layers_mapped) {
                const title = elementData.layers_mapped[elementData.layer].text;
                return titleBracketsOptionalDescription(title, elementData.description);
            }
            else {
                const title = elementData.all_layers?.find(layer => String(layer.value) === String(elementData.layer))?.text ?? t_layer_not_set;
                return titleBracketsOptionalDescription(title, elementData.description);
            }
        }

        case 'placeholder':
        case 'qrcode':
            return titleBracketsOptionalDescription(elementData.qrcode_url?.trim() || t_url_not_set, elementData.description);

        case 'streaming':
            return titleBracketsOptionalDescription(elementData.url?.trim() || t_url_not_set, elementData.description);

        case 'weather':
            return elementData.city ? titleBracketsOptionalDescription(elementData.city, elementData.description) : elementData.description;

        case 'youtube':
            return titleBracketsOptionalDescription(elementData.youtube_url?.trim() || t_url_not_set, elementData.description);

        case 'rssfeed':
            return titleBracketsOptionalDescription(elementData.rssfeed_url?.trim() || t_url_not_set, elementData.description);

        case 'emptyrectangle':
        case 'text':
            if (!elementDescriptionTemp) {
                elementDescriptionTemp = titles[elementData.type];
            }
        // The fallthrough to default is intended for emptyrectangle and text
        default:
            return elementData.name ? titleBracketsOptionalDescription(elementData.name, elementDescriptionTemp) : elementDescriptionTemp;
    }
}


/**
 * Sort list of elements in-place, modifying the {@link listReference}
 * @param listReference {[]} List to sort (will be modified)
 * @param nameProp {string} Property of the elements to sort on when {@link sortOption} is 0 or 1
 * @param sortOption {DragdropSortOption}
 * @returns {[]} Sorted list of elements
 */
export function sortElementList(listReference, nameProp, sortOption) {
    switch (sortOption) {
        case 0:
            listReference.sort((a, b) => a[nameProp].localeCompare(b[nameProp]));
            break;
        case 1:
            listReference.sort((a, b) => b[nameProp].localeCompare(a[nameProp]));
            break;
        case 2:
            listReference.sort((a, b) => b.updated_at_millis - a.updated_at_millis);
            break;
        case 3:
            listReference.sort((a, b) => a.updated_at_millis - b.updated_at_millis);
            break;
    }

    return listReference;
}

/** @type {Array<PublishElementDataCommon['type']>} */
const elementTypesWithPropertyDisplayRuns = ['video', 'ppt', 'pdf', 'campaign', 'template', 'widget', 'layer', 'youtube'];

/**
 * Check if element has property 'display_runs'
 * @param data {PublishElementData} Data of Publish Element
 * @returns {boolean} if data has property 'display_runs'
 */
export function hasPropertyDisplayRuns(
    data
) {
    return Object.keys(data).includes('display_runs');
}

/**
 * Calculate element duration (used in publish and content templates)
 * @param data {PublishElementData} Data of Publish Element
 * @param [defaultDuration] {number | string} (optional) default duration to use during calc
 * @returns {{duration: number, runs_text: string}} 'duration' in seconds and 'runs_text' to append
 */
export function calcElementDuration(
    data,
    defaultDuration = undefined
) {
    let duration = 0;
    let runs_text = '';

    if (hasPropertyDisplayRuns(data) && (data.duration_type == 2)) {
        const pagedDuration = calcPagedDuration(data);
        if (pagedDuration == '?') {
            duration = 0;
        } else {
            duration = pagedDuration * (parseInt(data.display_runs || '1') || 0);
        }
        runs_text = `${data.display_runs} ${Lang.langChoice('Run|Runs', data.display_runs)}`;
    } else if (data.duration_type == 0) {
        duration = parseInt(data.duration);
    } else if (defaultDuration) {
        duration = parseInt(defaultDuration);
    } else {
        duration = Laravel.default_duration;
    }

    return {
        duration,
        runs_text
    };
}

/**
 * Construct display text from calculated 'duration' and 'runs_text'
 * @param duration {number} Duration in seconds
 * @param runsText {string} Text to append at end
 * @returns {string} Text to display in frontend
 */
export function getDurationRunsDisplayText(duration, runsText) {
    const hasDuration = !!duration;
    const hasRunsText = !!runsText?.trim();

    if (hasDuration && hasRunsText) {
        return `${secondsToTimeString(duration)} (${runsText})`;
    }

    if (hasDuration) {
        return secondsToTimeString(duration);
    }

    if (hasRunsText) {
        return runsText;
    }

    return '';
}

/** @returns {string} */
export function getPublishFilterLocalStorageKey() {
    return `publish-filter.${Laravel.activeUser}.${Laravel.activeProject}`;
}

/** @returns {string} */
export function getLayerIdLocalStorageKey() {
    return `publish-layer-editor-id.${Laravel.activeUser}.${Laravel.activeProject}`
}
